import { AppError, getUserFriendlyMessage } from '@nvgc/shared';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
  persistent?: boolean;
  actions?: NotificationAction[];
  timestamp: Date;
}

export interface NotificationAction {
  label: string;
  action: () => void;
  primary?: boolean;
}

type NotificationListener = (notifications: Notification[]) => void;

class NotificationService {
  private notifications: Notification[] = [];
  private listeners: NotificationListener[] = [];
  private nextId = 1;

  /**
   * Subscribe to notification updates
   */
  subscribe(listener: NotificationListener): () => void {
    this.listeners.push(listener);
    
    // Send current notifications to new listener
    listener(this.notifications);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Show a success notification
   */
  success(message: string, options: Partial<Notification> = {}): string {
    return this.addNotification({
      type: 'success',
      message,
      duration: 4000,
      ...options
    });
  }

  /**
   * Show an error notification
   */
  error(error: AppError | string, options: Partial<Notification> = {}): string {
    const message = typeof error === 'string' ? error : getUserFriendlyMessage(error);
    const title = typeof error === 'string' ? 'Error' : `Error ${error.code}`;
    
    const actions: NotificationAction[] = [];
    
    // Add retry action for retryable errors
    if (typeof error !== 'string' && error.retryable && options.actions) {
      actions.push(...options.actions);
    }

    return this.addNotification({
      type: 'error',
      title,
      message,
      duration: 8000,
      persistent: typeof error !== 'string' && error.severity === 'critical',
      actions,
      ...options
    });
  }

  /**
   * Show a warning notification
   */
  warning(message: string, options: Partial<Notification> = {}): string {
    return this.addNotification({
      type: 'warning',
      message,
      duration: 6000,
      ...options
    });
  }

  /**
   * Show an info notification
   */
  info(message: string, options: Partial<Notification> = {}): string {
    return this.addNotification({
      type: 'info',
      message,
      duration: 4000,
      ...options
    });
  }

  /**
   * Remove a notification by ID
   */
  remove(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notifyListeners();
  }

  /**
   * Remove all notifications
   */
  clear(): void {
    this.notifications = [];
    this.notifyListeners();
  }

  /**
   * Get all current notifications
   */
  getAll(): Notification[] {
    return [...this.notifications];
  }

  /**
   * Add a notification
   */
  private addNotification(notification: Omit<Notification, 'id' | 'timestamp'>): string {
    const id = `notification-${this.nextId++}`;
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date()
    };

    this.notifications.unshift(newNotification);
    
    // Limit total notifications
    if (this.notifications.length > 10) {
      this.notifications = this.notifications.slice(0, 10);
    }

    this.notifyListeners();

    // Auto-remove non-persistent notifications
    if (!notification.persistent && notification.duration && notification.duration > 0) {
      setTimeout(() => {
        this.remove(id);
      }, notification.duration);
    }

    return id;
  }

  /**
   * Notify all listeners of changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.notifications);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }
}

export const notificationService = new NotificationService();

// Export singleton getter for backward compatibility
export const getNotificationService = () => notificationService;

// Convenience functions
export const notify = {
  success: (message: string, options?: Partial<Notification>) => 
    notificationService.success(message, options),
  
  error: (error: AppError | string, options?: Partial<Notification>) => 
    notificationService.error(error, options),
  
  warning: (message: string, options?: Partial<Notification>) => 
    notificationService.warning(message, options),
  
  info: (message: string, options?: Partial<Notification>) => 
    notificationService.info(message, options)
};